import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import {
  Loader2, ShieldAlert, MapPin, Calendar, Heart, Crown, Send, Mail,
  TrendingUp, Flag, Ban, MessageSquare, RotateCcw, Trash2, NotebookPen,
  Gift, AlertTriangle, ArrowLeft, Zap, Infinity,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${BACKEND_URL}/api/files/${p}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatCard({ label, value, sub, color = '#0F0F10', testId }) {
  return (
    <div className="bg-[var(--surface-2)] rounded-2xl p-3 border border-[var(--hairline)]" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{label}</p>
      <p className="font-display text-xl font-[700] tabular-nums mt-1" style={{ color }}>
        {typeof value === 'number' ? value.toLocaleString() : (value || '—')}
      </p>
      {sub && <p className="text-[10px] text-[#8A8A93] mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="px-5 mt-6">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={14} className="text-[#B91C1C]" />}
        <h2 className="text-[12px] uppercase tracking-wider font-bold text-[#4B4B52]">{title}</h2>
        <div className="ml-auto">{action}</div>
      </div>
      {children}
    </section>
  );
}

function tierBadgeStyle(tier) {
  if (tier === 'vip') return { bg: '#FDF7EC', color: '#B38138', border: '#B38138' };
  if (tier === 'premium') return { bg: '#EFF6FF', color: '#1D4ED8', border: '#3B82F6' };
  return { bg: '#F1F5F9', color: '#475569', border: '#94A3B8' };
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [compForm, setCompForm] = useState({ tier: 'premium', days: 30, lifetime: false });
  const [compHistory, setCompHistory] = useState(null);

  // Load past comps whenever the Comp Premium dialog opens. Keeps the payload
  // tiny (server caps at 20 rows) and prevents accidental double-grants.
  useEffect(() => {
    if (!showCompDialog || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/users/${userId}/comp-history`);
        if (!cancelled) setCompHistory(r.data);
      } catch { if (!cancelled) setCompHistory(null); }
    })();
    return () => { cancelled = true; };
  }, [showCompDialog, userId]);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [pushForm, setPushForm] = useState({ title: '', body: '', deeplink: '/discover' });

  useEffect(() => {
    api.get('/admin/me').then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}/details`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load user');
      navigate('/admin/users', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  const revokeComp = async () => {
    if (!window.confirm('Revoke this comp now? User will be downgraded to Free immediately.')) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}/comp-premium`);
      toast.success('Comp revoked · user is now Free');
      setCompHistory(null);
      setShowCompDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally { setBusy(false); }
  };

  const compPremium = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/comp-premium`, compForm);
      const label = compForm.lifetime ? 'FOREVER' : `${compForm.days} days`;
      toast.success(`Comped ${compForm.tier.toUpperCase()} · ${label}`);
      setShowCompDialog(false);
      setCompHistory(null); // reset so next open re-fetches
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to comp');
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/notes`, { text: noteText.trim() });
      toast.success('Note saved');
      setShowNoteDialog(false);
      setNoteText('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    } finally { setBusy(false); }
  };

  const sendPush = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/push`, pushForm);
      toast.success('Push sent');
      setShowPushDialog(false);
      setPushForm({ title: '', body: '', deeplink: '/discover' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send push');
    } finally { setBusy(false); }
  };

  const resetSwipes = async () => {
    if (!window.confirm('Reset daily swipe count for this user?')) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/reset-swipes`);
      toast.success('Swipes reset');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const removeUser = async () => {
    const reason = window.prompt(`Remove ${data?.profile?.display_name || userId}? Reason (optional):`);
    if (reason === null) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${userId}`, { data: { reason } });
      toast.success('Profile removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    } finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/reactivate`);
      toast.success('Profile reactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    } finally { setBusy(false); }
  };

  if (!authChecked || loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const { profile, subscription, stats, reports_against_list, admin_notes } = data;
  const isDeleted = !!profile.deleted_at;
  const tier = subscription?.tier || profile.tier || 'free';
  const tierStyle = tierBadgeStyle(tier);
  const photo = getPhotoUrl(profile.photo_url) || (profile.photos?.[0] ? getPhotoUrl(profile.photos[0]) : null);

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="admin-user-detail">
      {/* Header */}
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <button onClick={() => navigate('/admin/users')}
          className="w-9 h-9 rounded-full bg-[var(--surface-2)] hover:bg-black/[0.05] flex items-center justify-center transition"
          data-testid="back-to-users">
          <ArrowLeft size={16} />
        </button>
        <h1 className="font-brand text-[22px] font-[600] tracking-tight">User Deep Dive</h1>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11} /> Admin
        </span>
      </div>

      {/* Hero */}
      <div className="px-5 pt-5">
        <div className="flex items-start gap-4">
          <img
            src={photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`}
            alt=""
            className="w-20 h-20 rounded-2xl object-cover bg-[var(--surface-2)] flex-shrink-0 border border-[var(--hairline)]"
            onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`; }}
            data-testid="user-photo"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`font-display text-xl font-[700] truncate ${isDeleted ? 'line-through text-[#8A8A93]' : 'text-[#0F0F10]'}`}
                 data-testid="user-name">
                {profile.display_name || '(no name)'}{profile.age ? `, ${profile.age}` : ''}
              </p>
              {isDeleted && <span className="badge bg-black/[0.06] text-[#4B4B52] border-black/10">Removed</span>}
              {!profile.profile_complete && !isDeleted && <span className="badge bg-[#FDF7EC] text-[#B38138] border-[#B38138]/20">Incomplete</span>}
              <span className="badge"
                style={{ background: tierStyle.bg, color: tierStyle.color, borderColor: tierStyle.border + '40' }}
                data-testid="user-tier-badge">
                {tier === 'vip' && <Crown size={9} className="inline mr-1" />}
                {tier.toUpperCase()}
              </span>
            </div>
            <p className="text-[12px] text-[#8A8A93] mt-1 flex items-center gap-1.5 flex-wrap">
              <Mail size={10} /> {profile.email || '—'}
            </p>
            <p className="text-[12px] text-[#8A8A93] mt-1 flex items-center gap-1.5 flex-wrap">
              <MapPin size={10} /> {profile.location?.city || 'no city'} · {profile.gender || '—'}
            </p>
            <p className="text-[11px] text-[#8A8A93] mt-1 flex items-center gap-1.5 flex-wrap">
              <Calendar size={10} /> Joined {fmtDateOnly(profile.created_at)}
              {profile.last_active_at && ` · last active ${fmtDateOnly(profile.last_active_at)}`}
            </p>
            <p className="text-[11px] text-[#4B4B52] mt-1 font-mono break-all">{profile.user_id}</p>
          </div>
        </div>
      </div>

      {/* Quick action row */}
      <div className="px-5 mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button onClick={() => setShowCompDialog(true)} disabled={busy} data-testid="action-comp"
          className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#F5D48A] to-[#B38138] text-white text-[12px] font-bold shadow-sm hover:opacity-90 active:scale-95 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Gift size={13} /> Comp Premium
        </button>
        <button onClick={() => setShowNoteDialog(true)} disabled={busy} data-testid="action-note"
          className="px-3 py-2 rounded-xl bg-[#0F0F10] text-white text-[12px] font-bold shadow-sm hover:opacity-90 active:scale-95 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <NotebookPen size={13} /> Add Note
        </button>
        <button onClick={() => setShowPushDialog(true)} disabled={busy} data-testid="action-push"
          className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#1E40AF] text-white text-[12px] font-bold shadow-sm hover:opacity-90 active:scale-95 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Send size={13} /> Push
        </button>
        <button onClick={resetSwipes} disabled={busy} data-testid="action-reset-swipes"
          className="px-3 py-2 rounded-xl bg-white border border-[var(--hairline-strong)] text-[#4B4B52] text-[12px] font-bold hover:bg-black/[0.02] active:scale-95 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Zap size={13} /> Reset Swipes
        </button>
      </div>

      {/* Subscription */}
      <Section title="Subscription" icon={Crown}>
        {subscription ? (
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)] space-y-2 text-[13px]">
            <Row label="Tier" value={`${subscription.tier?.toUpperCase() || '—'}${subscription.billing ? ` · ${subscription.billing}` : ''}`} />
            <Row label="Status" value={subscription.status || '—'} valueClass={subscription.status === 'active' ? 'text-[#15803D]' : subscription.status === 'canceled' ? 'text-[#B91C1C]' : ''} />
            <Row label="Started" value={fmtDate(subscription.started_at)} />
            {subscription.expires_at && <Row label="Expires" value={fmtDate(subscription.expires_at)} />}
            {subscription.canceled_at && <Row label="Cancelled" value={fmtDate(subscription.canceled_at)} />}
            {subscription.comp_by && <Row label="Comped by" value={subscription.comp_by} />}
            {subscription.stripe_customer_id && <Row label="Stripe customer" value={subscription.stripe_customer_id} mono />}
            {subscription.stripe_subscription_id && <Row label="Stripe sub" value={subscription.stripe_subscription_id} mono />}
          </div>
        ) : (
          <p className="text-[13px] text-[#8A8A93] px-1">No subscription history. Free tier.</p>
        )}
      </Section>

      {/* Activity */}
      <Section title="Activity" icon={TrendingUp}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Matches" value={stats.matches} testId="stat-matches" />
          <StatCard label="Swipes" value={stats.swipes} sub={`${stats.swipes_right} right · ${stats.swipes_super} super`} testId="stat-swipes" />
          <StatCard label="Match rate" value={`${stats.match_rate}%`} color="#15803D" testId="stat-match-rate" />
          <StatCard label="Messages sent" value={stats.messages_sent} testId="stat-messages" />
          <StatCard label="Intros sent" value={stats.intros_sent} testId="stat-intros-sent" />
          <StatCard label="Intros received" value={stats.intros_received} testId="stat-intros-received" />
          <StatCard label="Intros accepted" value={stats.intros_accepted} testId="stat-intros-accepted" />
          <StatCard label="Last swipe" value={fmtDateOnly(stats.last_swipe_at)} testId="stat-last-swipe" />
        </div>
        <p className="text-[11px] text-[#8A8A93] mt-2 px-1">
          Last message: {fmtDate(stats.last_message_at)}
        </p>
      </Section>

      {/* Safety */}
      <Section title="Safety & Reports" icon={Flag}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <StatCard label="Reports against" value={stats.reports_against} color={stats.reports_against > 0 ? '#B91C1C' : '#0F0F10'} testId="stat-reports-against" />
          <StatCard label="Reports filed" value={stats.reports_filed} testId="stat-reports-filed" />
          <StatCard label="Blocked by" value={stats.blocked_by} color={stats.blocked_by > 3 ? '#B91C1C' : '#0F0F10'} testId="stat-blocked-by" />
          <StatCard label="Blocked others" value={stats.blocked_others} testId="stat-blocked-others" />
        </div>
        {reports_against_list.length === 0 ? (
          <p className="text-[12px] text-[#8A8A93] px-1">No reports filed against this user.</p>
        ) : (
          <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] divide-y divide-[var(--hairline)]" data-testid="reports-list">
            {reports_against_list.map((r) => (
              <div key={r.report_id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] font-semibold text-[#B91C1C] flex items-center gap-1">
                    <AlertTriangle size={11} /> {r.reason}
                  </p>
                  <p className="text-[10px] text-[#8A8A93]">{fmtDateOnly(r.created_at)}</p>
                </div>
                {r.description && <p className="text-[12px] text-[#4B4B52] mt-1 line-clamp-3">{r.description}</p>}
                {r.status === 'resolved' && <p className="text-[10px] text-[#15803D] mt-1">✓ resolved · {r.resolution || ''}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Admin notes */}
      <Section title="Admin Notes" icon={NotebookPen}>
        {admin_notes.length === 0 ? (
          <p className="text-[12px] text-[#8A8A93] px-1">No notes yet. Use “Add Note” to record interactions.</p>
        ) : (
          <div className="space-y-2" data-testid="notes-list">
            {admin_notes.map((n) => (
              <div key={n.note_id} className="bg-[var(--surface-2)] rounded-xl p-3 border border-[var(--hairline)]">
                <p className="text-[13px] text-[#0F0F10] whitespace-pre-wrap">{n.text}</p>
                <p className="text-[10px] text-[#8A8A93] mt-1">{n.by || 'admin'} · {fmtDate(n.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Danger zone */}
      <Section title="Danger Zone" icon={Ban}>
        {isDeleted ? (
          <button onClick={reactivate} disabled={busy} data-testid="action-reactivate"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#15803D]/30 text-[#15803D] text-[13px] font-bold hover:bg-[#15803D]/[0.06] active:scale-[0.99] transition inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <RotateCcw size={14} /> Reactivate Profile
          </button>
        ) : (
          <button onClick={removeUser} disabled={busy} data-testid="action-remove"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#B91C1C]/30 text-[#B91C1C] text-[13px] font-bold hover:bg-[#B91C1C]/[0.06] active:scale-[0.99] transition inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Trash2 size={14} /> Remove Profile
          </button>
        )}
      </Section>

      {/* Comp Premium dialog */}
      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Comp Premium</DialogTitle><DialogDescription>Grant a user free Premium or VIP — for a fixed number of days, or forever.</DialogDescription></DialogHeader>

          {/* Comp history — audit trail so admins can spot double-comps */}
          {compHistory && (compHistory.total_grants > 0 || compHistory.current?.status === 'comp') && (
            <div className="mt-2 rounded-xl border border-[var(--hairline)] bg-[#FAFAFA] p-3" data-testid="comp-history-block">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Comp history</p>
                <p className="text-[10px] text-[#8A8A93]">
                  <span className="font-semibold text-[#0F0F10]" data-testid="comp-history-total">{compHistory.total_grants}</span> grant{compHistory.total_grants === 1 ? '' : 's'}
                  {compHistory.total_days_granted > 0 && <> · <span className="font-semibold text-[#0F0F10]">{compHistory.total_days_granted}</span>d total</>}
                  {compHistory.lifetime_grants > 0 && <> · <span className="font-semibold text-[#B45309]" data-testid="comp-history-lifetime-count">{compHistory.lifetime_grants}</span> lifetime</>}
                </p>
              </div>

              {/* Active-comp warning banner */}
              {compHistory.current?.status === 'comp' && (
                <div className="mb-2 px-2.5 py-2 rounded-lg bg-[#FEF3C7] border border-[#B45309]/30 flex items-center justify-between gap-2" data-testid="comp-history-active-warning">
                  <p className="text-[11px] text-[#78350F] font-semibold flex items-center gap-1 min-w-0">
                    <AlertTriangle size={11} strokeWidth={2.4} className="shrink-0" />
                    <span className="truncate">
                      Active: {compHistory.current.tier?.toUpperCase()}
                      {compHistory.current.is_lifetime ? ' · LIFETIME' :
                        compHistory.current.expires_at ? ` · until ${new Date(compHistory.current.expires_at).toLocaleDateString()}` : ''}
                    </span>
                  </p>
                  <button
                    onClick={revokeComp}
                    disabled={busy}
                    data-testid="comp-revoke-btn"
                    className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-[#B91C1C] text-white hover:bg-[#991B1B] disabled:opacity-50 transition"
                  >
                    Revoke
                  </button>
                </div>
              )}

              {compHistory.history.length > 0 ? (
                <div className="space-y-1 max-h-[120px] overflow-y-auto" data-testid="comp-history-list">
                  {compHistory.history.slice(0, 5).map((h, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-2 text-[11px]" data-testid={`comp-history-row-${i}`}>
                      <div className="flex items-baseline gap-1.5 min-w-0">
                        {h.revoke ? <span className="text-[#B91C1C] shrink-0 font-bold text-[9px]">✕</span> :
                          h.lifetime ? <Infinity size={10} className="text-[#B45309] shrink-0" strokeWidth={2.4} /> : null}
                        <span className={`font-medium truncate ${h.revoke ? 'text-[#B91C1C] line-through decoration-[#B91C1C]/40' : 'text-[#0F0F10]'}`}>
                          {h.revoke ? h.text : h.text.replace('Comped ', '')}
                        </span>
                      </div>
                      <span className="text-[#8A8A93] shrink-0 tabular-nums">
                        {h.created_at ? new Date(h.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  ))}
                  {compHistory.history.length > 5 && (
                    <p className="text-[10px] text-[#8A8A93] italic pt-0.5">+ {compHistory.history.length - 5} more</p>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-[#8A8A93] italic">No previous comps.</p>
              )}
            </div>
          )}
          <div className="space-y-4 py-2">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#8A8A93] mb-2">Tier</p>
              <div className="flex gap-2">
                {['premium', 'vip'].map((t) => (
                  <button key={t} onClick={() => setCompForm({ ...compForm, tier: t })} data-testid={`comp-tier-${t}`}
                    className={`flex-1 px-3 py-2 rounded-xl text-[12px] font-bold border transition ${
                      compForm.tier === t ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'
                    }`}>
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#8A8A93] mb-2">Duration</p>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {[7, 30, 90, 365].map((d) => (
                  <button key={d} onClick={() => setCompForm({ ...compForm, days: d, lifetime: false })} data-testid={`comp-days-${d}`}
                    className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold border transition ${
                      !compForm.lifetime && compForm.days === d ? 'bg-[#0F0F10] text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'
                    }`}>{d}d</button>
                ))}
              </div>
              <button
                onClick={() => setCompForm({ ...compForm, lifetime: true })}
                data-testid="comp-lifetime"
                className={`w-full px-3 py-2 mb-2 rounded-lg text-[12px] font-bold border transition inline-flex items-center justify-center gap-1.5 ${
                  compForm.lifetime
                    ? 'bg-gradient-to-r from-[#B45309] to-[#D97706] text-white border-transparent shadow-sm'
                    : 'bg-white border-[#B45309]/40 text-[#B45309] hover:bg-[#FEF3C7]'
                }`}>
                <Infinity size={14} strokeWidth={2.4} /> Lifetime / Forever
              </button>
              <input type="number" min="1" max="365" value={compForm.days}
                onChange={(e) => setCompForm({ ...compForm, days: Math.max(1, Math.min(365, parseInt(e.target.value || 0))), lifetime: false })}
                disabled={compForm.lifetime}
                className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30 disabled:opacity-40 disabled:bg-[#FAFAFA]"
                data-testid="comp-days-input" />
              {compForm.lifetime && (
                <p className="text-[10px] text-[#B45309] mt-1.5 font-medium" data-testid="comp-lifetime-note">
                  Lifetime grant — subscription never expires. Use sparingly.
                </p>
              )}
            </div>
            <button onClick={compPremium} disabled={busy} data-testid="comp-confirm"
              className="w-full px-4 py-3 rounded-xl brand-gradient text-white text-[13px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift size={14} />}
              Comp {compForm.tier.toUpperCase()} {compForm.lifetime ? 'FOREVER' : `for ${compForm.days} days`}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Admin Note</DialogTitle><DialogDescription>Internal note attached to this user, only visible to admins.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <textarea value={noteText} onChange={(e) => setNoteText(e.target.value.slice(0, 2000))}
              rows={5} placeholder="What happened? Context, action taken, follow-up needed…"
              className="w-full p-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30 resize-none"
              data-testid="note-textarea" />
            <p className="text-[10px] text-[#8A8A93] text-right">{noteText.length}/2000</p>
            <button onClick={addNote} disabled={busy || !noteText.trim()} data-testid="note-confirm"
              className="w-full px-4 py-3 rounded-xl bg-[#0F0F10] text-white text-[13px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <NotebookPen size={14} />} Save Note
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Push dialog */}
      <Dialog open={showPushDialog} onOpenChange={setShowPushDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Send Push</DialogTitle><DialogDescription>Send a push notification directly to this user’s subscribed devices.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <input value={pushForm.title} onChange={(e) => setPushForm({ ...pushForm, title: e.target.value.slice(0, 80) })}
              placeholder="Title (e.g. We miss you ❤)"
              className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
              data-testid="push-title" />
            <textarea value={pushForm.body} onChange={(e) => setPushForm({ ...pushForm, body: e.target.value.slice(0, 160) })}
              rows={3} placeholder="Body (max 160 chars)"
              className="w-full p-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30 resize-none"
              data-testid="push-body" />
            <input value={pushForm.deeplink} onChange={(e) => setPushForm({ ...pushForm, deeplink: e.target.value })}
              placeholder="Deeplink (default /discover)"
              className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
              data-testid="push-deeplink" />
            <button onClick={sendPush} disabled={busy || !pushForm.title.trim() || !pushForm.body.trim()} data-testid="push-confirm"
              className="w-full px-4 py-3 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#1E40AF] text-white text-[13px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />} Send Push
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, valueClass = '', mono }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <p className="text-[#8A8A93] flex-shrink-0">{label}</p>
      <p className={`font-semibold text-right break-all ${mono ? 'font-mono text-[11px]' : ''} ${valueClass}`}>{value}</p>
    </div>
  );
}
